import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';

import { PUBLIC_ROUTES, ROUTES } from './routes';
import { useSession } from './session/useSession';
import { AppLayout } from './ui/AppLayout';
import { NotFound } from './ui/NotFound';
import { ForcedPasswordPage, ForgotPasswordPage, SetPasswordPage } from './ui/password/PasswordPages';
import { SCREENS } from './ui/screens';
import { SignInPage } from './ui/SignInPage';
import { VerifyPage } from './ui/document/VerifyPage';
import { ForcedTwoFactorPage } from './ui/twofactor/TwoFactorCard';

/** D68 : chargée à la demande, elle ne pèse rien sur la coquille. */
const InsertionPage = lazy(async () => ({ default: (await import('./ui/relations/InsertionPage')).InsertionPage }));

export function App(): ReactNode {
    const { session, t } = useSession();

    // Trois états, pas deux. Sans l'état « chargement », le formulaire de
    // connexion s'afficherait une fraction de seconde à chaque rechargement de
    // page, AVANT que le rafraîchissement silencieux n'aboutisse — et
    // l'utilisateur croirait avoir été déconnecté.
    if (session.status === 'loading') {
        return <p className="boot">{t('session.loading')}</p>;
    }

    return (
        <BrowserRouter>
            <Routes>
                {/*
                    Le lien du courriel s'ouvre dans TOUS les états : le
                    titulaire peut très bien avoir une session ouverte sur ce
                    navigateur — celle d'un collègue, sur un poste partagé.
                */}
                <Route path={PUBLIC_ROUTES.setPassword} element={<SetPasswordPage />} />
                {/* D61 : la vérification d'un document, ouverte à tous, connecté ou non. */}
                <Route path={PUBLIC_ROUTES.verifyDocument} element={<VerifyPage />} />
                <Route path={`${PUBLIC_ROUTES.verifyDocument}/:reference`} element={<VerifyPage />} />
                {/* D68 : l'enquête d'insertion d'un lauréat, ouverte par le lien de son courriel. */}
                <Route
                    path={`${PUBLIC_ROUTES.insertionSurvey}/:token`}
                    element={
                        <Suspense fallback={<p className="boot">{t('session.loading')}</p>}>
                            <InsertionPage />
                        </Suspense>
                    }
                />

                {session.status === 'anonymous' ? (
                    <>
                        <Route path={PUBLIC_ROUTES.forgotPassword} element={<ForgotPasswordPage />} />
                        {/*
                            Toute autre adresse affiche la connexion SANS
                            changer d'URL : une fois connecté, l'utilisateur
                            arrive là où son lien voulait le mener.
                        */}
                        <Route path="*" element={<SignInPage />} />
                    </>
                ) : session.me.mustChangePassword ? (
                    // D32 : rien d'autre tant que le provisoire n'est pas changé.
                    // Le serveur refuse de toute façon ; ici, on n'affiche pas
                    // une coquille dont chaque écran répondrait 403.
                    <Route path="*" element={<ForcedPasswordPage />} />
                ) : session.me.twoFactorSetupRequired ? (
                    // D39 : la politique exige l'application ; rien d'autre avant.
                    <Route path="*" element={<ForcedTwoFactorPage />} />
                ) : (
                    <Route element={<AppLayout me={session.me} />}>
                        {ROUTES.map((route) => {
                            const Screen = SCREENS[route.screen];

                            return (
                                <Route
                                    key={route.path}
                                    path={route.path}
                                    element={
                                        <Suspense fallback={<p className="muted">{t('common.loading')}</p>}>
                                            <Screen />
                                        </Suspense>
                                    }
                                />
                            );
                        })}

                        {/*
                            Le catch-all sert le 404 DE L'APPLICATION, pas celui
                            d'Apache : le serveur renvoie index.html pour toute URL
                            inconnue, c'est donc ici que se décide ce que voit
                            l'utilisateur. Les assets, eux, répondent bien 404 —
                            FallbackResource est désactivé sous /assets.
                        */}
                        <Route path="*" element={<NotFound />} />
                    </Route>
                )}
            </Routes>
        </BrowserRouter>
    );
}
